import { loadMapboxSDK } from 'services/LazyLoading';
import Config from 'services/config/local';

/* global mapboxgl */
/* global MapboxGeocoder */

/* @ngInject */
export default class LocationModalController {
    constructor($scope, DialogService, IAMService, ProjectManager, ToastService, projects, allProjects) {
        this.$scope = $scope;
        this.DialogService = DialogService;
        this.IAMService = IAMService;
        this.ProjectManager = ProjectManager;
        this.ToastService = ToastService;

        // Projects without timezone (shown in the list)
        this.projects = [...projects];
        // All projects (shown on the map)
        this.allProjects = [...allProjects];

        this.filteredProjects = this.projects;
        this.query = '';

        // Map state
        this.map = null;
        this.popup = null;
        this.marker = null;
        this.allFeatures = [];
        this.editingLocation = false;
        this.projectBeingEdited = null;
        this.timeZoneToBeConfirmed = null;
        this.loadingTimeZone = false;
        this.readyToSaveLocation = false;
        this.showConfirmLocationDialog = false;

        this.updatedProjects = [];

        this.initMap();
    }

    get projectsWithLocationCount() {
        return this.allProjects.filter(p => p.location && p.location.latitude !== null && p.location.longitude !== null).length;
    }

    initMap() {
        loadMapboxSDK().then(() => {
            let initialLocation = [0, 35];
            let initialZoom = 0.8;

            const projectLocations = this.allProjects
                .filter(project => project.location && project.location.latitude !== null && project.location.longitude !== null)
                .map(project => [project.location.longitude, project.location.latitude]);

            if (projectLocations.length > 0) {
                const resultBounds = projectLocations.reduce((bounds, coord) => {
                    return bounds.extend(coord);
                }, new mapboxgl.LngLatBounds(projectLocations[0], projectLocations[0]));
                initialLocation = resultBounds.getCenter().toArray();
            }

            if (projectLocations.length > 1) {
                const resultBounds = projectLocations.reduce((bounds, coord) => {
                    return bounds.extend(coord);
                }, new mapboxgl.LngLatBounds(projectLocations[0], projectLocations[0]));

                const northeast = resultBounds.getNorthEast();
                const southwest = resultBounds.getSouthWest();
                const latSpan = northeast.lat - southwest.lat;
                const lngSpan = northeast.lng - southwest.lng;
                const maxSpan = Math.max(latSpan, lngSpan);

                initialZoom = this.calculateZoomLevel(maxSpan);
            } else if (projectLocations.length === 1) {
                initialZoom = 8;
            }

            mapboxgl.accessToken = Config.mapbox.accessToken;
            this.map = new mapboxgl.Map({
                container: 'location-modal-map',
                style: 'mapbox://styles/mapbox/streets-v12',
                center: initialLocation,
                zoom: initialZoom,
                projection: 'mercator'
            });

            this.map.on('load', () => {
                this.updateMap();
                this.setupMapListeners();

                setTimeout(() => {
                    window.dispatchEvent(new Event('resize'));
                }, 50);
                setTimeout(() => {
                    window.dispatchEvent(new Event('resize'));
                }, 200);
            });
        });
    }

    calculateZoomLevel(maxSpan) {
        if (maxSpan > 100) return 1;
        if (maxSpan > 50) return 2;
        if (maxSpan > 20) return 3;
        if (maxSpan > 10) return 4;
        if (maxSpan > 5) return 5;
        if (maxSpan > 2) return 6;
        if (maxSpan > 1) return 7;
        return 8;
    }

    setupMapListeners() {
        this.isMouseOverMarkerOrPopup = false;
        this.marker = new mapboxgl.Marker(); // eslint-disable-line

        this.map.on('mouseenter', 'unclustered-point', (e) => {
            this.map.getCanvas().style.cursor = 'pointer';
            const coordinates = e.features[0].geometry.coordinates.slice();
            const description = e.features[0].properties.description;

            while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
            }

            this.popup.setLngLat(coordinates).setHTML(`
                <div class="popup-content">
                    <div class="popup-title">${this.escapeHtml(e.features[0].properties.name)}</div>
                    <div class="popup-description">${description}</div>
                </div>
            `).addTo(this.map);

            this.isMouseOverMarkerOrPopup = true;

            const popupContent = document.querySelector('.location-modal .mapboxgl-popup-content');
            if (popupContent) {
                popupContent.addEventListener('mouseenter', () => {
                    this.isMouseOverMarkerOrPopup = true;
                });
                popupContent.addEventListener('mouseleave', () => {
                    this.isMouseOverMarkerOrPopup = false;
                    setTimeout(() => {
                        if (!this.isMouseOverMarkerOrPopup) {
                            this.popup.remove();
                        }
                    }, 300);
                });
            }
        });

        this.map.on('mouseleave', 'unclustered-point', () => {
            this.map.getCanvas().style.cursor = '';
            this.isMouseOverMarkerOrPopup = false;
            setTimeout(() => {
                if (!this.isMouseOverMarkerOrPopup) {
                    this.popup.remove();
                }
            }, 300);
        });

        this.map.on('click', (e) => {
            if (!this.editingLocation) return;

            this.getTimeZone(e.lngLat);
            this.marker.remove();

            this.marker = new mapboxgl.Marker()
                .setLngLat([e.lngLat.lng, e.lngLat.lat])
                .setDraggable(false)
                .addTo(this.map);
        });

        this.geocoder = new MapboxGeocoder({
            accessToken: mapboxgl.accessToken,
            mapboxgl: mapboxgl,
            marker: false
        });
        this.map.addControl(this.geocoder);

        this.geocoder.on('result', (e) => {
            const lngLat = e.result.center;
            this.map.flyTo({ center: lngLat, zoom: 15 });
        });
    }

    updateMap() {
        const locations = [];
        this.allProjects.forEach(project => {
            if (!project.location || project.location.latitude === null || project.location.longitude === null) return;
            locations.push({
                coords: [project.location.longitude, project.location.latitude],
                name: project.displayName,
                description: `Sensors: <b>${project.sensorCount}</b><br>Cloud Connectors: <b>${project.cloudConnectorCount}</b>`,
                id: project.id || project.name
            });
        });

        this.allFeatures = locations.map(loc => ({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: loc.coords },
            properties: { name: loc.name, description: loc.description, id: loc.id }
        }));

        const geojson = {
            type: 'FeatureCollection',
            features: this.allFeatures
        };

        this.map.addSource('markers', {
            type: 'geojson',
            data: geojson,
            cluster: true,
            clusterMaxZoom: 14,
            clusterRadius: 18
        });

        this.map.addLayer({
            id: 'clusters',
            type: 'circle',
            source: 'markers',
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': '#58ADEA',
                'circle-radius': 20
            }
        });

        this.map.addLayer({
            id: 'cluster-count',
            type: 'symbol',
            source: 'markers',
            filter: ['has', 'point_count'],
            layout: {
                'text-field': '{point_count_abbreviated}',
                'text-font': ['Open Sans Bold', 'DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12
            },
            paint: {
                'text-color': '#fff'
            }
        });

        this.map.addLayer({
            id: 'unclustered-point',
            type: 'circle',
            source: 'markers',
            filter: ['!', ['has', 'point_count']],
            paint: {
                'circle-color': '#58ADEA',
                'circle-radius': 8,
                'circle-stroke-width': 2,
                'circle-stroke-color': '#fff'
            }
        });

        this.popup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false
        });

        this.$scope.$applyAsync();
    }

    rebuildMapLayers() {
        this.map.removeLayer('unclustered-point');
        this.map.removeLayer('cluster-count');
        this.map.removeLayer('clusters');
        this.map.removeSource('markers');
        this.updateMap();
    }

    queryChanged() {
        if (this.query === '') {
            this.filteredProjects = this.projects;
            return;
        }
        this.filteredProjects = this.projects.filter(project =>
            project.displayName.toLowerCase().includes(this.query.toLowerCase())
        );
    }

    clearQuery() {
        this.query = '';
        this.queryChanged();
    }

    showPopup(project) {
        const projectId = project.id || project.name;
        const feature = this.allFeatures.find(f => f.properties.id === projectId);
        if (feature) {
            this.map.flyTo({
                duration: 800,
                center: feature.geometry.coordinates,
                zoom: this.map.getZoom() > 4.5 ? this.map.getZoom() : 4.5
            });
            this.popup.setLngLat(feature.geometry.coordinates).setHTML(`
                <div class="popup-content">
                    <div class="popup-title">${this.escapeHtml(feature.properties.name)}</div>
                    <div class="popup-description">${feature.properties.description}</div>
                </div>
            `).addTo(this.map);
        }
    }

    editLocation(project) {
        this.editingLocation = true;
        this.projectBeingEdited = project;

        this.map.setPaintProperty('unclustered-point', 'circle-opacity', [
            'case',
            ['==', ['get', 'name'], project.displayName], 0.5,
            1
        ]);

        if (this.popup) {
            this.popup.remove();
        }
        this.readyToSaveLocation = false;
        this.showConfirmLocationDialog = true;
        this.$scope.$applyAsync();
    }

    getTimeZone(lngLat) {
        const lat = lngLat.lat;
        const lng = lngLat.lng;
        this.readyToSaveLocation = false;
        this.loadingTimeZone = true;
        this.$scope.$applyAsync();

        fetch(`https://api.mapbox.com/v4/examples.4ze9z6tv/tilequery/${lng},${lat}.json?access_token=${mapboxgl.accessToken}`) // eslint-disable-line
            .then(response => response.json())
            .then(data => {
                if (data && data.features && data.features.length > 0) {
                    this.timeZoneToBeConfirmed = data.features[0].properties.TZID;
                    this.readyToSaveLocation = true;
                } else {
                    this.marker.remove();
                    this.ToastService.showSimpleTranslated('project_no_valid_timezone_found');
                }
            })
            .catch(error => {
                console.error('Error fetching time zone:', error); // eslint-disable-line no-console
                this.ToastService.showSimpleTranslated('project_timezone_wasnt_updated');
            }).finally(() => {
                this.loadingTimeZone = false;
                this.$scope.$applyAsync();
            });
    }

    confirmNewLocation() {
        const newLocation = this.marker.getLngLat();
        this.IAMService.updateProject(this.projectBeingEdited.name, {
            location: {
                timeLocation: this.timeZoneToBeConfirmed,
                latitude: newLocation.lat,
                longitude: newLocation.lng
            }
        }).then((updatedProject) => {
            // Update in allProjects
            const allIndex = this.allProjects.findIndex(p => p.name === this.projectBeingEdited.name);
            if (allIndex !== -1) {
                this.allProjects[allIndex].location = updatedProject.location;
            }

            // Remove from projects-without-timezone list
            this.projects = this.projects.filter(p => p.name !== this.projectBeingEdited.name);
            this.filteredProjects = this.projects;
            this.queryChanged();

            this.updatedProjects.push(updatedProject);

            this.marker.remove();
            this.rebuildMapLayers();
            this.resetEditState();

            // Update current project if it was the one being edited
            if (updatedProject.name === this.ProjectManager.currentProject.name) {
                this.ProjectManager.setCurrentProject(updatedProject);
            }
        }).catch(error => {
            console.error('Error updating project location:', error); // eslint-disable-line no-console
            this.ToastService.showSimpleTranslated('project_timezone_wasnt_updated');
        });
    }

    cancelLocationEdit() {
        if (this.marker) {
            this.marker.remove();
        }

        this.map.setPaintProperty('unclustered-point', 'circle-opacity', [
            'case',
            ['==', ['get', 'name'], ''], 1,
            1
        ]);

        this.map.setLayoutProperty('unclustered-point', 'visibility', 'visible');
        this.map.setLayoutProperty('clusters', 'visibility', 'visible');
        this.map.setLayoutProperty('cluster-count', 'visibility', 'visible');

        this.resetEditState();
    }

    resetEditState() {
        this.editingLocation = false;
        this.projectBeingEdited = null;
        this.showConfirmLocationDialog = false;
        this.readyToSaveLocation = false;
        this.$scope.$applyAsync();
    }

    projectHasLocation(project) {
        return project.location && project.location.latitude !== null && project.location.longitude !== null;
    }

    truncateDisplayName(name) {
        return name && name.length > 28 ? `${name.substring(0, 28)}...` : name;
    }

    escapeHtml(str) {
        const div = document.createElement('div');
        div.appendChild(document.createTextNode(str));
        return div.innerHTML;
    }

    closeDialog() {
        if (this.map) {
            this.map.remove();
        }
        this.DialogService.hide(this.updatedProjects);
    }
}
